import { z } from "zod";
import { dateOnlySchema, listQuerySchema, pageSchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { csvArraySchema, queryBooleanSchema, timestampsShape, userRefSchema } from "./common.ts";
import { fileSchema } from "./files.ts";

/**
 * CREATIVE APPROVALS & PROOFING (PRD §8 Item 11, US-11.2…US-11.7).
 *
 * The shape of the module in one paragraph: a **deliverable** is the thing the
 * client is being asked to approve; a **version** is an immutable snapshot of
 * it (v1, v2, v3 — never overwritten); a **proof comment** is feedback pinned
 * to a point on a version (image coordinates, a video timecode, a PDF page); an
 * **approval** is the evidence row for one decision (who, which version, when,
 * from which IP and browser); and a **revision round** counts each
 * "changes requested" against the engagement's included rounds so the AM sees
 * the moment a job goes out of scope.
 *
 * Coordinates are integers in BASIS POINTS of the asset's width/height
 * (0…10000), never floats: a pin that round-trips through JSON must land on the
 * same pixel it was dropped on.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Idea → in review → (changes requested ⇄ in review) → approved → published. */
export const DELIVERABLE_STATUSES = ["draft", "in_review", "changes_requested", "approved", "published"] as const;
export type DeliverableStatus = (typeof DELIVERABLE_STATUSES)[number];
export const deliverableStatusSchema = z.enum(DELIVERABLE_STATUSES);

/** What a comment is pinned to. `none` is a plain thread comment. */
export const PROOF_PIN_KINDS = ["none", "image", "video", "page"] as const;
export type ProofPinKind = (typeof PROOF_PIN_KINDS)[number];
export const proofPinKindSchema = z.enum(PROOF_PIN_KINDS);

/** `internal` is never rendered on any client-facing surface (US-9.4). */
export const PROOF_VISIBILITIES = ["internal", "client"] as const;
export type ProofVisibility = (typeof PROOF_VISIBILITIES)[number];
export const proofVisibilitySchema = z.enum(PROOF_VISIBILITIES);

export const APPROVAL_DECISIONS = ["approved", "changes_requested"] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];
export const approvalDecisionSchema = z.enum(APPROVAL_DECISIONS);

/** Where a decision came from: the staff app, the logged-in portal, or a no-login link. */
export const APPROVAL_SOURCES = ["staff", "portal", "link"] as const;
export type ApprovalSource = (typeof APPROVAL_SOURCES)[number];
export const approvalSourceSchema = z.enum(APPROVAL_SOURCES);

export const APPROVAL_REQUEST_STATUSES = ["open", "approved", "changes_requested", "cancelled", "expired"] as const;
export type ApprovalRequestStatus = (typeof APPROVAL_REQUEST_STATUSES)[number];
export const approvalRequestStatusSchema = z.enum(APPROVAL_REQUEST_STATUSES);

/** Who wrote a comment or took a decision. A `guest` acted through a signed link with no contact record. */
export const PROOF_AUTHOR_KINDS = ["member", "contact", "guest", "system"] as const;
export type ProofAuthorKind = (typeof PROOF_AUTHOR_KINDS)[number];
export const proofAuthorKindSchema = z.enum(PROOF_AUTHOR_KINDS);

/** Pin coordinates are basis points of the asset's width / height: 0…10000, integers. */
export const PIN_BPS_MAX = 10000;

/** The two nudges US-11.7 promises, in hours after the request was sent. */
export const APPROVAL_NUDGE_HOURS = [24, 48] as const;

/** How long a no-login proof link stays valid, unless the caller asks for less. */
export const PROOF_LINK_DEFAULT_DAYS = 30;
export const PROOF_LINK_MAX_DAYS = 90;

// ---------------------------------------------------------------------------
// Deliverables
// ---------------------------------------------------------------------------

export const proofAuthorSchema = z.object({
  kind: proofAuthorKindSchema,
  userId: uuidSchema.nullable(),
  contactId: uuidSchema.nullable(),
  name: z.string(),
  email: z.string().nullable(),
});
export type ProofAuthor = z.infer<typeof proofAuthorSchema>;

/** One item going through review. `roundsUsed` counts "changes requested" cycles (US-11.5). */
export const deliverableSchema = z.object({
  id: uuidSchema,
  projectId: uuidSchema,
  clientId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  title: z.string(),
  description: z.string().nullable(),
  deliverableType: z.string(),
  status: deliverableStatusSchema,
  currentVersionId: uuidSchema.nullable(),
  currentVersionNo: z.number().int(),
  versionCount: z.number().int(),
  dueDate: dateOnlySchema.nullable(),
  approvalDueAt: timestampSchema.nullable(),
  assigneeUserId: uuidSchema.nullable(),
  assignee: userRefSchema.nullable(),
  roundsUsed: z.number().int(),
  outOfScopeRounds: z.number().int(),
  approvedVersionId: uuidSchema.nullable(),
  approvedAt: timestampSchema.nullable(),
  publishedAt: timestampSchema.nullable(),
  openCommentCount: z.number().int(),
  openRequestCount: z.number().int(),
  quotaRecordedAt: timestampSchema.nullable(),
  tags: z.array(z.string()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type DeliverableDto = z.infer<typeof deliverableSchema>;

/** An immutable snapshot. A new upload is always a NEW row — v1 never changes (US-11.4). */
export const deliverableVersionSchema = z.object({
  id: uuidSchema,
  deliverableId: uuidSchema,
  versionNo: z.number().int(),
  fileId: uuidSchema.nullable(),
  file: fileSchema.nullable(),
  notes: z.string().nullable(),
  uploadedBy: userRefSchema.nullable(),
  uploadedByName: z.string(),
  isCurrent: z.boolean(),
  isApproved: z.boolean(),
  commentCount: z.number().int(),
  createdAt: timestampSchema,
});
export type DeliverableVersionDto = z.infer<typeof deliverableVersionSchema>;

/** Feedback pinned to a point on one version. Coordinates round-trip as integers. */
export const proofCommentSchema = z.object({
  id: uuidSchema,
  deliverableId: uuidSchema,
  versionId: uuidSchema,
  versionNo: z.number().int(),
  parentId: uuidSchema.nullable(),
  body: z.string(),
  pinKind: proofPinKindSchema,
  /** Basis points of the asset width (0…10000). Null unless `pinKind` is image or page. */
  xBps: z.number().int().nullable(),
  yBps: z.number().int().nullable(),
  /** Milliseconds into the video. Null unless `pinKind` is video. */
  timecodeMs: z.number().int().nullable(),
  /** 1-based page. Null unless `pinKind` is page. */
  pageNumber: z.number().int().nullable(),
  visibility: proofVisibilitySchema,
  author: proofAuthorSchema,
  resolvedAt: timestampSchema.nullable(),
  resolvedBy: userRefSchema.nullable(),
  replyCount: z.number().int(),
  ...timestampsShape,
});
export type ProofCommentDto = z.infer<typeof proofCommentSchema>;

/** US-11.6: the dispute record. Append-only — a decision is never edited. */
export const deliverableApprovalSchema = z.object({
  id: uuidSchema,
  deliverableId: uuidSchema,
  versionId: uuidSchema,
  versionNo: z.number().int(),
  roundNo: z.number().int(),
  decision: approvalDecisionSchema,
  source: approvalSourceSchema,
  approver: proofAuthorSchema,
  comment: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  decidedAt: timestampSchema,
});
export type DeliverableApprovalDto = z.infer<typeof deliverableApprovalSchema>;

/** One named approver asked to decide, with its own link, deadline and nudge trail. */
export const approvalRequestSchema = z.object({
  id: uuidSchema,
  deliverableId: uuidSchema,
  versionId: uuidSchema,
  versionNo: z.number().int(),
  roundNo: z.number().int(),
  status: approvalRequestStatusSchema,
  recipient: proofAuthorSchema,
  message: z.string().nullable(),
  dueAt: timestampSchema.nullable(),
  sentAt: timestampSchema.nullable(),
  viewedAt: timestampSchema.nullable(),
  nudgeCount: z.number().int(),
  lastNudgeAt: timestampSchema.nullable(),
  decidedAt: timestampSchema.nullable(),
  expiresAt: timestampSchema.nullable(),
  /** Present ONLY in the response to "request approval", so the AM can paste it into WhatsApp. */
  url: z.string().nullable(),
  createdAt: timestampSchema,
});
export type ApprovalRequestDto = z.infer<typeof approvalRequestSchema>;

/** US-11.5: one "changes requested" cycle, flagged when it passes what was sold. */
export const revisionRoundSchema = z.object({
  id: uuidSchema,
  deliverableId: uuidSchema,
  deliverableTitle: z.string(),
  projectId: uuidSchema,
  clientId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  versionId: uuidSchema.nullable(),
  roundNo: z.number().int(),
  includedRounds: z.number().int(),
  outOfScope: z.boolean(),
  requestedBy: proofAuthorSchema,
  comment: z.string().nullable(),
  chargeNote: z.string().nullable(),
  chargedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type RevisionRoundDto = z.infer<typeof revisionRoundSchema>;

/** GET /api/v1/deliverables/:id → the whole proofing surface in one call. */
export const deliverableDetailSchema = deliverableSchema.extend({
  includedRevisionRounds: z.number().int(),
  versions: z.array(deliverableVersionSchema),
  comments: z.array(proofCommentSchema),
  approvals: z.array(deliverableApprovalSchema),
  requests: z.array(approvalRequestSchema),
  revisionRounds: z.array(revisionRoundSchema),
});
export type DeliverableDetailDto = z.infer<typeof deliverableDetailSchema>;

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/** Shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const deliverableWritableShape = {
  projectId: uuidSchema,
  engagementId: uuidSchema.nullable(),
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(5000).nullable(),
  deliverableType: z.string().trim().min(1).max(80),
  dueDate: dateOnlySchema.nullable(),
  assigneeUserId: uuidSchema.nullable(),
  tags: tagsSchema,
};

/** POST /api/v1/deliverables (perm `projects:write`) → 201 Deliverable. Unknown project → 404. */
export const createDeliverableInputSchema = z
  .object({
    ...deliverableWritableShape,
    engagementId: deliverableWritableShape.engagementId.optional(),
    description: deliverableWritableShape.description.optional(),
    deliverableType: deliverableWritableShape.deliverableType.optional(),
    dueDate: deliverableWritableShape.dueDate.optional(),
    assigneeUserId: deliverableWritableShape.assigneeUserId.optional(),
    tags: deliverableWritableShape.tags.optional(),
    /** Attach v1 in the same call — the file must already be uploaded. */
    fileId: uuidSchema.optional(),
    versionNotes: z.string().trim().max(2000).optional(),
  })
  .strict();
export type CreateDeliverableInput = z.input<typeof createDeliverableInputSchema>;

/** PATCH /api/v1/deliverables/:id (perm `projects:write`) → Deliverable. Merge patch. */
export const updateDeliverableInputSchema = z
  .object({ ...deliverableWritableShape, status: deliverableStatusSchema })
  .omit({ projectId: true })
  .partial()
  .strict();
export type UpdateDeliverableInput = z.input<typeof updateDeliverableInputSchema>;

/** POST /api/v1/deliverables/:id/versions (perm `projects:write`) → 201 Version. Never overwrites. */
export const createDeliverableVersionInputSchema = z
  .object({
    fileId: uuidSchema.nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .strict();
export type CreateDeliverableVersionInput = z.input<typeof createDeliverableVersionInputSchema>;

/** One approver on a request: a portal contact, a member, or a bare email address. */
export const approvalRecipientInputSchema = z
  .object({
    contactId: uuidSchema.optional(),
    userId: uuidSchema.optional(),
    email: z.string().trim().toLowerCase().email().max(254).optional(),
    name: z.string().trim().max(120).optional(),
  })
  .strict()
  .refine((v) => Boolean(v.contactId || v.userId || v.email), { message: "Name a contact, a member or an email address." });
export type ApprovalRecipientInput = z.input<typeof approvalRecipientInputSchema>;

/** POST /api/v1/deliverables/:id/request-approval (perm `projects:write`) → the requests, with links. */
export const requestApprovalInputSchema = z
  .object({
    recipients: z.array(approvalRecipientInputSchema).min(1).max(20),
    versionId: uuidSchema.optional(),
    message: z.string().trim().max(2000).optional(),
    dueAt: timestampSchema.optional(),
    /** Link validity in days (default 30, max 90). */
    expiresInDays: z.number().int().min(1).max(PROOF_LINK_MAX_DAYS).optional(),
    /** Skip the emails — the AM will paste the link into WhatsApp themselves. */
    silent: z.boolean().optional(),
  })
  .strict();
export type RequestApprovalInput = z.input<typeof requestApprovalInputSchema>;

/** POST /api/v1/deliverables/:id/decision (perm `projects:write`) → Deliverable detail. */
export const recordDecisionInputSchema = z
  .object({
    decision: approvalDecisionSchema,
    versionId: uuidSchema.optional(),
    comment: z.string().trim().max(4000).optional(),
  })
  .strict();
export type RecordDecisionInput = z.input<typeof recordDecisionInputSchema>;

const pinShape = {
  pinKind: proofPinKindSchema,
  xBps: z.number().int().min(0).max(PIN_BPS_MAX).nullable(),
  yBps: z.number().int().min(0).max(PIN_BPS_MAX).nullable(),
  timecodeMs: z.number().int().min(0).nullable(),
  pageNumber: z.number().int().min(1).max(10000).nullable(),
};

/** POST /api/v1/deliverables/:id/comments (perm `messages:write`) → 201 Comment. */
export const createProofCommentInputSchema = z
  .object({
    body: z.string().trim().min(1).max(8000),
    versionId: uuidSchema.optional(),
    parentId: uuidSchema.optional(),
    visibility: proofVisibilitySchema.optional(),
    pinKind: pinShape.pinKind.optional(),
    xBps: pinShape.xBps.optional(),
    yBps: pinShape.yBps.optional(),
    timecodeMs: pinShape.timecodeMs.optional(),
    pageNumber: pinShape.pageNumber.optional(),
  })
  .strict();
export type CreateProofCommentInput = z.input<typeof createProofCommentInputSchema>;

/** PATCH /api/v1/proof-comments/:id (perm `messages:write`) → Comment. Only the body and the pin move. */
export const updateProofCommentInputSchema = z
  .object({ body: z.string().trim().min(1).max(8000), resolved: z.boolean(), ...pinShape })
  .partial()
  .strict();
export type UpdateProofCommentInput = z.input<typeof updateProofCommentInputSchema>;

/** PATCH /api/v1/revision-rounds/:id (perm `projects:write`) → the round, flagged as charged. */
export const updateRevisionRoundInputSchema = z
  .object({ chargeNote: z.string().trim().max(500).nullable(), charged: z.boolean() })
  .partial()
  .strict();
export type UpdateRevisionRoundInput = z.input<typeof updateRevisionRoundInputSchema>;

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** GET /api/v1/deliverables — the proofing queue, filtered the way the board filters it. */
export const listProofsQuerySchema = listQuerySchema.extend({
  projectId: uuidSchema.optional(),
  clientId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  assigneeUserId: uuidSchema.optional(),
  status: csvArraySchema(deliverableStatusSchema, DELIVERABLE_STATUSES.length).optional(),
  deliverableType: z.string().trim().max(80).optional(),
  dueBefore: dateOnlySchema.optional(),
  includeArchived: queryBooleanSchema.optional(),
  sort: z.enum(["createdAt", "dueDate", "title", "status"]).optional(),
});
export type ListProofsQuery = z.infer<typeof listProofsQuerySchema>;

/** GET /api/v1/deliverables/:id/comments — internal comments are dropped for portal callers. */
export const listProofCommentsQuerySchema = z.object({
  versionId: uuidSchema.optional(),
  visibility: proofVisibilitySchema.optional(),
  resolved: queryBooleanSchema.optional(),
});
export type ListProofCommentsQuery = z.infer<typeof listProofCommentsQuerySchema>;

/** GET /api/v1/revision-rounds — the scope-creep report (US-11.5). */
export const listRevisionRoundsQuerySchema = listQuerySchema.extend({
  clientId: uuidSchema.optional(),
  projectId: uuidSchema.optional(),
  engagementId: uuidSchema.optional(),
  deliverableId: uuidSchema.optional(),
  outOfScope: queryBooleanSchema.optional(),
  charged: queryBooleanSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export type ListRevisionRoundsQuery = z.infer<typeof listRevisionRoundsQuerySchema>;

/** Per-client turnaround, the number US-11.7 feeds into health and the weekly report. */
export const approvalTurnaroundSchema = z.object({
  clientId: uuidSchema.nullable(),
  requested: z.number().int(),
  decided: z.number().int(),
  approved: z.number().int(),
  changesRequested: z.number().int(),
  pending: z.number().int(),
  overdue: z.number().int(),
  avgHoursToDecide: z.number().nullable(),
  outOfScopeRounds: z.number().int(),
});
export type ApprovalTurnaroundDto = z.infer<typeof approvalTurnaroundSchema>;

export const approvalTurnaroundQuerySchema = z.object({
  clientId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});
export type ApprovalTurnaroundQuery = z.infer<typeof approvalTurnaroundQuerySchema>;

export const deliverablePageSchema = pageSchema(deliverableSchema);
export const revisionRoundPageSchema = pageSchema(revisionRoundSchema);

// ---------------------------------------------------------------------------
// The no-login proof link (US-11.3, "2 taps to decide")
// ---------------------------------------------------------------------------

export const proofTokenParamsSchema = z.object({ token: z.string().min(40).max(200) });
export type ProofTokenParams = z.infer<typeof proofTokenParamsSchema>;

/**
 * An EARLIER version, as the approver may revisit it (US-11.4): what it looked
 * like, what the team said about it, and the client-visible feedback left on
 * it. Read-only — decisions and new comments only ever apply to the version
 * the request names.
 */
export const publicProofPreviousVersionSchema = z.object({
  versionId: uuidSchema,
  versionNo: z.number().int(),
  notes: z.string().nullable(),
  createdAt: timestampSchema,
  /** Short-lived signed URL, like the current version's; null when that version carried no file. */
  assetUrl: z.string().nullable(),
  assetFilename: z.string().nullable(),
  assetContentType: z.string().nullable(),
  comments: z.array(proofCommentSchema),
});
export type PublicProofPreviousVersionDto = z.infer<typeof publicProofPreviousVersionSchema>;

/**
 * What an unauthenticated approver sees. Deliberately narrower than the staff
 * DTO: no internal comments, no assignee, no engagement, no revision economics.
 */
export const publicProofViewSchema = z.object({
  deliverableId: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  status: deliverableStatusSchema,
  agencyName: z.string(),
  agencyLogoUrl: z.string().nullable(),
  clientName: z.string().nullable(),
  projectName: z.string().nullable(),
  versionId: uuidSchema,
  versionNo: z.number().int(),
  versionNotes: z.string().nullable(),
  /** Short-lived signed URL for the asset; null when the version carries no file. */
  assetUrl: z.string().nullable(),
  assetFilename: z.string().nullable(),
  assetContentType: z.string().nullable(),
  message: z.string().nullable(),
  dueAt: timestampSchema.nullable(),
  expiresAt: timestampSchema,
  approverName: z.string(),
  /** Null until this approver decides; then the page shows the receipt instead of the buttons. */
  decision: approvalDecisionSchema.nullable(),
  decidedAt: timestampSchema.nullable(),
  canDecide: z.boolean(),
  comments: z.array(proofCommentSchema),
  /** Versions BEFORE the one under review, newest first. Never a later one: that has its own request. */
  previousVersions: z.array(publicProofPreviousVersionSchema),
});
export type PublicProofViewDto = z.infer<typeof publicProofViewSchema>;

/** POST /api/public/v1/proofs/:token/decision — two taps, same evidence as the portal. */
export const publicProofDecisionInputSchema = z
  .object({
    decision: approvalDecisionSchema,
    comment: z.string().trim().max(4000).optional(),
  })
  .strict();
export type PublicProofDecisionInput = z.input<typeof publicProofDecisionInputSchema>;

/** POST /api/public/v1/proofs/:token/comments — a pinned change request from the client. */
export const publicProofCommentInputSchema = z
  .object({
    body: z.string().trim().min(1).max(8000),
    parentId: uuidSchema.optional(),
    pinKind: pinShape.pinKind.optional(),
    xBps: pinShape.xBps.optional(),
    yBps: pinShape.yBps.optional(),
    timecodeMs: pinShape.timecodeMs.optional(),
    pageNumber: pinShape.pageNumber.optional(),
  })
  .strict();
export type PublicProofCommentInput = z.input<typeof publicProofCommentInputSchema>;
